import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react';
import type { CierreConfirmado } from '../hooks/useCierreConfirmado';

type ModalProps = {
  open: boolean;
  title?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
} & (
  // Ventanas de solo lectura: se cierran enseguida.
  | { onClose: () => void; cierre?: undefined }
  // Formularios: `useCierreConfirmado` pide confirmación si hay datos sin guardar.
  | { cierre: CierreConfirmado; onClose?: undefined }
);

const sizeMap = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
  // Formularios con tablas anchas (p. ej. los ítems de una OC): casi todo el ancho de la pantalla
  // (en el celular, como las demás, ocupa toda la pantalla).
  full: 'sm:max-w-[min(1600px,96vw)]',
};

// Ventanas abiertas, en orden de apertura: Escape actúa solo sobre la de arriba (la última).
const ventanasAbiertas: object[] = [];

/**
 * Suma a la pila de ventanas algo que atiende Escape (una ventana o la burbuja de la ayuda interactiva) mientras está
 * abierto. Devuelve si es el de arriba: sólo ése reacciona a Escape.
 */
export function useVentanaApilada(abierta: boolean): () => boolean {
  // Efecto aparte del de Escape: ese se vuelve a suscribir en cada render y no debe cambiar el orden de la pila.
  const ventana = useRef({});
  useEffect(() => {
    if (!abierta) return;
    const esta = ventana.current;
    ventanasAbiertas.push(esta);
    return () => { ventanasAbiertas.splice(ventanasAbiertas.indexOf(esta), 1); };
  }, [abierta]);
  return useCallback(() => ventanasAbiertas[ventanasAbiertas.length - 1] === ventana.current, []);
}

export function Modal({ open, onClose, cierre, title, children, size = 'lg' }: ModalProps) {
  const titleId = useId();
  const avisoId = useId();
  const cerrar = cierre ? cierre.cerrar : onClose!;
  const confirmando = open && !!cierre?.confirmando;

  const esLaDeArriba = useVentanaApilada(open);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!esLaDeArriba()) return;
      // Escape sobre la confirmación equivale a "Seguir editando".
      if (cierre?.confirmando) cierre.seguirEditando();
      else cerrar();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, cierre, cerrar, esLaDeArriba]);

  // Mientras se confirma, el foco va a "Seguir editando"; al volver, regresa a donde estaba.
  const seguirRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!confirmando) return;
    const previo = document.activeElement as HTMLElement | null;
    seguirRef.current?.focus();
    return () => { if (previo?.isConnected) previo.focus(); };
  }, [confirmando]);

  if (!open) return null;

  // A click outside the dialog does not close it: most dialogs are forms and would lose what was typed.
  // Closing is explicit, with the ✕ or Escape.
  return (
    <div className="fixed inset-0 z-[60] modal-backdrop flex items-start justify-center p-0 sm:p-4 sm:pt-10">
      <div
        className={`relative bg-white rounded-none sm:rounded-2xl shadow-xl w-full h-full sm:h-auto ${sizeMap[size]} max-h-full sm:max-h-[85vh] flex flex-col`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
      >
        {title && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
            <h2 id={titleId} className="text-lg font-semibold text-slate-800">{title}</h2>
            <button
              onClick={cerrar}
              aria-label="Cerrar"
              className="text-slate-400 hover:text-slate-600 transition-colors text-xl leading-none p-2 -mr-2 min-w-[2.5rem] min-h-[2.5rem] flex items-center justify-center shrink-0"
            >
              ✕
            </button>
          </div>
        )}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">{children}</div>
        {confirmando && (
          // Dentro de la misma ventana (no otra encima), tapando el formulario hasta que se elija.
          <div className="absolute inset-0 z-20 flex items-center justify-center p-4 bg-white/80 rounded-none sm:rounded-2xl">
            <div
              role="alertdialog"
              aria-modal="true"
              aria-labelledby={avisoId}
              className="w-full max-w-sm bg-white border border-slate-200 rounded-xl shadow-lg p-5"
            >
              <p id={avisoId} className="text-sm font-medium text-slate-800">Hay datos sin guardar. ¿Descartarlos?</p>
              <div className="flex justify-end gap-3 mt-4">
                <button
                  ref={seguirRef}
                  onClick={cierre!.seguirEditando}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-500"
                >
                  Seguir editando
                </button>
                <button
                  onClick={cierre!.descartar}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50"
                >
                  Descartar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
